import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trpc } from '../utils/trpc';
import styles from '../styles/GroupJoinRequests.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RequestRow {
  id: string;
  profile_id: string;
  joined_at: string;
  status: string;
  member_profiles:
    | { id: string; username: string; profile_picture_url: string | null }
    | { id: string; username: string; profile_picture_url: string | null }[]
    | null;
}

// ─── Utils ────────────────────────────────────────────────────────────────────

function getTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

const AVATAR_GRADIENTS = [
  'linear-gradient(145deg,#fcb31b,#e67919)',
  'linear-gradient(145deg,#4a7a47,#80af7d)',
  'linear-gradient(145deg,#2d4f2b,#4a7a47)',
  'linear-gradient(145deg,#e67919,#fcb31b)',
];

function Avatar({
  username,
  url,
  size = 40,
}: {
  username: string;
  url?: string | null;
  size?: number;
}) {
  const idx = username.charCodeAt(0) % AVATAR_GRADIENTS.length;
  if (url) {
    return (
      <img
        onLoad={(e) => e.currentTarget.classList.add('loaded')}
        src={url}
        alt={username}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
        }}
      />
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: AVATAR_GRADIENTS[idx],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Borsok', sans-serif",
        fontSize: size * 0.4,
        color: '#fff',
        flexShrink: 0,
      }}
    >
      {username.charAt(0).toUpperCase()}
    </div>
  );
}

// ─── Request Card ─────────────────────────────────────────────────────────────

function RequestCard({
  req,
  onApprove,
  onDeny,
  approving,
  denying,
}: {
  req: RequestRow;
  onApprove: (profileId: string) => void;
  onDeny: (profileId: string) => void;
  approving: boolean;
  denying: boolean;
}) {
  const profile = Array.isArray(req.member_profiles)
    ? req.member_profiles[0]
    : req.member_profiles;

  const username = profile?.username ?? 'Unknown';
  const timeAgo = getTimeAgo(req.joined_at);

  return (
    <div className={styles.requestCard}>
      <div className={styles.requestLeft}>
        <Avatar username={username} url={profile?.profile_picture_url} size={44} />
        <div className={styles.requestInfo}>
          <span className={styles.requestUsername}>{username}</span>
          <span className={styles.requestTime}>Requested {timeAgo}</span>
        </div>
      </div>
      <div className={styles.requestActions}>
        <button
          className={styles.denyBtn}
          onClick={() => onDeny(req.profile_id)}
          disabled={approving || denying}
        >
          {denying ? '...' : 'Deny'}
        </button>
        <button
          className={styles.approveBtn}
          onClick={() => onApprove(req.profile_id)}
          disabled={approving || denying}
        >
          {approving ? '...' : 'Approve'}
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function GroupJoinRequests() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const [pendingAction, setPendingAction] = useState<{
    profileId: string;
    type: 'approve' | 'deny';
  } | null>(null);

  const { data: group } = trpc.group.getById.useQuery({ id: id! }, { enabled: !!id });

  const {
    data: requests = [],
    isLoading,
    error,
  } = trpc.group.getJoinRequests.useQuery({ groupId: id! }, { enabled: !!id });

  const approveMutation = trpc.group.approveJoinRequest.useMutation({
    onSuccess: () => {
      utils.group.getJoinRequests.invalidate({ groupId: id! });
      utils.group.getMembers.invalidate({ groupId: id! });
      utils.group.getById.invalidate({ id: id! });
      setPendingAction(null);
    },
  });

  const denyMutation = trpc.group.denyJoinRequest.useMutation({
    onSuccess: () => {
      utils.group.getJoinRequests.invalidate({ groupId: id! });
      setPendingAction(null);
    },
  });

  const handleApprove = (profileId: string) => {
    setPendingAction({ profileId, type: 'approve' });
    approveMutation.mutate({ groupId: id!, targetUserId: profileId });
  };

  const handleDeny = (profileId: string) => {
    setPendingAction({ profileId, type: 'deny' });
    denyMutation.mutate({ groupId: id!, targetUserId: profileId });
  };

  const handleApproveAll = () => {
    requests.forEach((r: RequestRow) =>
      approveMutation.mutate({ groupId: id!, targetUserId: r.profile_id }),
    );
  };

  const handleDenyAll = () => {
    requests.forEach((r: RequestRow) =>
      denyMutation.mutate({ groupId: id!, targetUserId: r.profile_id }),
    );
  };

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
          Loading requests...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState}>
          {error.message.includes('UNAUTHORIZED')
            ? "You don't have permission to view join requests."
            : 'Something went wrong.'}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* ── Header ── */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate(`/group/${id}`)}>
          ← Back to group
        </button>

        <div className={styles.headerInner}>
          <div className={styles.headerLeft}>
            <div className={styles.headerAvatar}>
              {group?.name?.charAt(0).toUpperCase() ?? 'G'}
            </div>
            <div className={styles.headerText}>
              <h1 className={styles.headerTitle}>Join Requests</h1>
              <span className={styles.headerSub}>{group?.name}</span>
            </div>
          </div>

          {requests.length > 0 && (
            <div className={styles.bulkActions}>
              <button
                className={styles.denyAllBtn}
                onClick={handleDenyAll}
                disabled={denyMutation.isPending || approveMutation.isPending}
              >
                Deny all
              </button>
              <button
                className={styles.approveAllBtn}
                onClick={handleApproveAll}
                disabled={approveMutation.isPending || denyMutation.isPending}
              >
                Approve all ({requests.length})
              </button>
            </div>
          )}
        </div>

        <div className={styles.headerDivider} />
      </div>

      {/* ── Content ── */}
      <div className={styles.content}>
        {requests.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path
                  d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" />
                <path
                  d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <p className={styles.emptyTitle}>No pending requests</p>
            <p className={styles.emptyText}>
              When someone requests to join this private group, they'll appear here.
            </p>
          </div>
        ) : (
          <div className={styles.requestList}>
            <div className={styles.listHeader}>
              <span className={styles.listCount}>
                {requests.length} pending {requests.length === 1 ? 'request' : 'requests'}
              </span>
            </div>

            {requests.map((req: RequestRow) => {
              const isApproving =
                pendingAction?.profileId === req.profile_id &&
                pendingAction.type === 'approve' &&
                approveMutation.isPending;
              const isDenying =
                pendingAction?.profileId === req.profile_id &&
                pendingAction.type === 'deny' &&
                denyMutation.isPending;

              return (
                <RequestCard
                  key={req.id}
                  req={req}
                  onApprove={handleApprove}
                  onDeny={handleDeny}
                  approving={isApproving}
                  denying={isDenying}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
